import { API_LANGUAGE } from '../../api/client';
import { formatDateTime, timeAgo } from '../../utils/format';

/** Pure helpers turning station API data into what the Gas Stations screen renders. */

export const IS_DUTCH = API_LANGUAGE === 'du';
const NUMBER_LOCALE = IS_DUTCH ? 'nl-NL' : 'en-GB';

const priceFormat = new Intl.NumberFormat(NUMBER_LOCALE, {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});

/** 1.879 → "€ 1,879" (nl) / "€1.879" (en). Non-numbers → "—". */
export function formatPrice(value) {
  return typeof value === 'number' && Number.isFinite(value) ? priceFormat.format(value) : '—';
}

const UNIT_LABELS = { L: 'per Liter (L)', kWh: 'per kWh', kg: 'per Kilogram (kg)' };
const UNIT_SHORT = { L: '/ L', kWh: '/ kWh', kg: '/ kg' };
export const unitLabel = (unit) => UNIT_LABELS[unit] ?? (unit ? `per ${unit}` : '');
export const unitShort = (unit) => UNIT_SHORT[unit] ?? (unit ? `/ ${unit}` : '');

/** Relative time plus the local date-time for a tooltip. */
export function describeTime(iso) {
  if (!iso) return { relative: '—', exact: '' };
  return { relative: timeAgo(iso), exact: formatDateTime(iso) };
}

export const CATEGORIES = [
  { key: 'petrol', label: 'Petrol' },
  { key: 'diesel', label: 'Diesel' },
  { key: 'ev', label: 'EV Charging' },
  { key: 'lpg', label: 'LPG' },
  { key: 'cng', label: 'CNG' },
];
export const categoryLabel = (key) => CATEGORIES.find((c) => c.key === key)?.label ?? key;

/** Filter chips, in toolbar order. `none` is only shown when it has stations. */
export const HUB_FILTERS = [
  { key: 'all', label: 'All Stations', dot: null },
  { key: 'fuel', label: 'Petrol / Diesel Hubs', dot: 'bg-amber-600' },
  { key: 'ev', label: 'EV Charging Only', dot: 'bg-secondary' },
  { key: 'hybrid', label: 'Hybrid Hubs (Fuel + EV)', dot: 'bg-teal-500' },
  { key: 'lpg_cng', label: 'LPG / CNG Network', dot: 'bg-blue-600' },
  { key: 'none', label: 'No fuel sold', dot: 'bg-outline', hideWhenEmpty: true },
];
export const HUB_TYPES = HUB_FILTERS.map((h) => h.key);

const HUB_BADGES = {
  fuel: { label: 'Gas Station', icon: 'local_gas_station', iconClass: 'text-amber-600' },
  ev: { label: 'EV Charging', icon: 'ev_station', iconClass: 'text-secondary' },
  evFast: { label: 'EV Ultra-Fast', icon: 'bolt', iconClass: 'text-secondary' },
  hybrid: { label: 'Hybrid Hub', icon: 'alt_route', iconClass: 'text-teal-600' },
  lpg_cng: { label: 'Gas & CNG Hub', icon: 'propane', iconClass: 'text-blue-600' },
  none: { label: 'No fuel sold', icon: 'block', iconClass: 'text-outline' },
};

const isDcPriced = (f) => f.category === 'ev' && /(^|_)dc($|_)/i.test(f.code) && f.isAvailable && f.latestPrice != null;

/** Hub badge; EV stations with a priced DC option show as "EV Ultra-Fast". */
export function hubBadge(station) {
  if (station.hubType === 'ev' && station.fuels.some(isDcPriced)) return HUB_BADGES.evFast;
  return HUB_BADGES[station.hubType] ?? { label: station.hubType, icon: 'help', iconClass: 'text-outline' };
}

/** "Shell Express Sloterdijk" → "SE"; no name → null (the avatar shows an icon instead). */
export function stationInitials(name) {
  const words = String(name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return null;
  return words
    .slice(0, 2)
    .map((w) => Array.from(w)[0].toUpperCase())
    .join('');
}

/**
 * How a fuel chip looks: 'latest' (highlighted), 'priced', 'noPrice' or 'notSold'.
 * @param {import('./stationSchema').StationFuel} fuel
 */
export function chipState(fuel, latestFuelOptionId) {
  if (!fuel.isAvailable) return 'notSold';
  if (fuel.latestPrice == null) return 'noPrice';
  return fuel.fuelOptionId === latestFuelOptionId ? 'latest' : 'priced';
}

/** Option name in the admin's language. */
export const optionName = (option) => (IS_DUTCH && option.name_du ? option.name_du : option.name_en);

/**
 * Converts a save/reset response (`{ fuels: [{ ..., entry }] }`) to the list row's
 * `fuels` shape, so the table can update before the refetch lands.
 */
export function entriesToStationFuels(fuelsWithEntries) {
  return fuelsWithEntries
    .filter((f) => f.entry)
    .map((f) => ({
      fuelOptionId: f.fuelOptionId,
      code: f.code,
      category: f.category,
      name: IS_DUTCH && f.name_du ? f.name_du : f.name_en,
      unit: f.unit,
      latestPrice: f.entry.latestPrice,
      isAvailable: f.entry.isAvailable,
      source: f.entry.source,
      scanCount: f.entry.scanCount,
      updatedAt: f.entry.updatedAt,
      updatedBy: f.entry.updatedBy,
    }));
}

/** Active options grouped by category in display order (for selects and the taxonomy cards). */
export function groupOptions(options) {
  const active = options.filter((o) => o.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
  return CATEGORIES.map((c) => {
    const items = active.filter((o) => o.category === c.key);
    return { ...c, unit: items[0]?.unit ?? null, options: items };
  }).filter((group) => group.options.length > 0);
}
